import { type Mesh, MeshBuilder, Vector3 } from "@babylonjs/core";
import { HeroId, Slot, hv, pk } from "@rivalwatch/shared";
import { baked } from "../../audio/bake.ts";
import * as loops from "../../audio/cues/loops.ts";
import type { Cue } from "../../audio/engine.ts";
import type { Theme } from "../../audio/theory.ts";
import { bell, click, fm, later, mallet, noise, pluck, thump, tone, trim, whoosh } from "../../audio/synth.ts";
import { glow } from "../../game/materials.ts";
import type { ClientHero, ProjectileFrame } from "../types.ts";

const V = { orbs: 0, orbTimer: 1, passive: 2, ultPulse: 3 } as const;
const PK = { Thorn: pk(HeroId.Mantissa, 0), Spore: pk(HeroId.Mantissa, 1) } as const;
const hz = (semi: number, base = 293.66) => base * 2 ** (semi / 12);

interface Gear {
  antennaL: Mesh;
  antennaR: Mesh;
  wingL: Mesh;
  wingR: Mesh;
  eyes: Mesh[];
  orbs: Mesh[];
  aura: Mesh;
}

const thornMath: Cue = baked(
  "fire:mantissa:thorn-polish",
  () =>
    trim(15.4, (o) => {
      click(o, 0.18, 5200, 0.003);
      pluck(o, hz(21), 0.18, 0.7);
      whoosh(o, 0.14, 1100, 2800, 0.12);
      return 0.26;
    }),
  { variants: 5, jitter: 0.015 },
);
const kindBloom: Cue = trim(6.3, (o) => {
  [0, 4, 7].forEach((s, i) => mallet(later(o, i * 0.05), hz(s + 12), 0.12, 0.65));
  noise(o, { color: "pink", type: "bandpass", f: 900, f1: 2600, q: 1.4, gain: 0.22, a: 0.06, d: 0.38 });
  return 0.8;
});
const pepPollen: Cue = trim(11.8, (o) => {
  bell(o, hz(16), 0.18, 0.46);
  bell(later(o, 0.07), hz(21), 0.18, 0.42);
  tone(o, { f: hz(4), f1: hz(9), glide: 0.2, gain: 0.07, d: 0.28 });
  return 0.65;
});
const napSpore: Cue = trim(12.4, (o) => {
  click(o, 0.16, 3600, 0.004);
  whoosh(o, 0.24, 1500, 500, 0.32);
  fm(o, { f: hz(24), f1: hz(17), ratio: 2.2, index: 1.2, index1: 0, gain: 0.09, d: 0.38 });
  return 0.75;
});
const pricklyMood: Cue = trim(2.6, (o) => {
  thump(o, 0.18, 140, 80, 0.12);
  fm(o, { f: hz(9), ratio: 1.5, index: 2.6, index1: 0.4, gain: 0.09, d: 0.3 });
  noise(o, { color: "crackle", type: "bandpass", f: 2200, gain: 0.14, d: 0.16 });
  return 0.55;
});
const gardenOverdrive: Cue = trim(3.6, (o) => {
  thump(o, 0.34, 100, 42, 0.25);
  [0, 2, 4, 7, 9].forEach((s, i) => bell(later(o, i * 0.045), hz(s + 12), 0.2, 0.52));
  noise(o, { color: "pink", type: "bandpass", f: 700, f1: 2400, q: 1.1, gain: 0.3, a: 0.1, d: 0.5 });
  return 1.15;
});
const mantissaTheme = {
  bpm: 108,
  octave: 0,
  voice: pluck,
  double: (o, f, d, v) => void bell(later(o, 0.01), f * 2, d * 0.8, v * 0.35),
  notes: [
    [0, 0.5],
    [2, 0.5],
    [7, 1],
    [4, 0.5],
    [9, 1.5],
  ],
} satisfies Theme;

const hero: ClientHero<Gear> = {
  id: HeroId.Mantissa,
  bulk: 0.9,
  shape: { head: 1.12, arms: 1.08 },
  gear(g) {
    const { scene, R, H, rig, add, addWorld, glow: gglow } = g;
    const antennaL = add(MeshBuilder.CreateCylinder("antenna-l", { diameter: 0.035, height: H * 0.3, tessellation: 6 }, scene), rig.head, gglow("#d7ff5a"));
    antennaL.position.set(-R * 0.32, R * 0.82, 0);
    antennaL.rotation.z = -0.35;
    const antennaR = add(MeshBuilder.CreateCylinder("antenna-r", { diameter: 0.035, height: H * 0.3, tessellation: 6 }, scene), rig.head, gglow("#d7ff5a"));
    antennaR.position.set(R * 0.32, R * 0.82, 0);
    antennaR.rotation.z = 0.35;
    const wingL = add(MeshBuilder.CreateBox("wing-l", { width: R * 0.75, height: H * 0.46, depth: 0.035 }, scene), rig.torso, gglow("#baff6a", 0.25));
    wingL.position.set(-R * 0.45, -H * 0.04, R * 0.72);
    wingL.rotation.z = -0.25;
    const wingR = add(MeshBuilder.CreateBox("wing-r", { width: R * 0.75, height: H * 0.46, depth: 0.035 }, scene), rig.torso, gglow("#baff6a", 0.25));
    wingR.position.set(R * 0.45, -H * 0.04, R * 0.72);
    wingR.rotation.z = 0.25;
    const eyes = [-1, 1].map((side) => {
      const eye = add(MeshBuilder.CreateSphere(`mantissa-eye-${side}`, { diameter: 0.08, segments: 8 }, scene), rig.head, gglow("#d7ff5a", 0.9));
      eye.position.set(side * R * 0.22, R * 0.08, -R * 0.5);
      return eye;
    });
    const orbs = [0, 1, 2, 3].map((i) => addWorld(MeshBuilder.CreateSphere(`life-orb-${i}`, { diameter: 0.16, segments: 10 }, scene), gglow("#d7ff5a", 0.82)));
    const aura = addWorld(MeshBuilder.CreateTorus("garden-ring", { diameter: 30, thickness: 0.055, tessellation: 80 }, scene), gglow("#7ed957", 0.18));
    aura.setEnabled(false);
    return { antennaL, antennaR, wingL, wingR, eyes, orbs, aura };
  },
  animate(gear, f) {
    const flap = Math.sin(f.t * 7) * 0.18;
    gear.wingL.rotation.y = flap;
    gear.wingR.rotation.y = -flap;
    gear.antennaL.rotation.x = Math.sin(f.t * 3) * 0.08;
    gear.antennaR.rotation.x = -Math.sin(f.t * 3) * 0.08;
    for (let i = 0; i < gear.orbs.length; i++) {
      const orb = gear.orbs[i]!;
      const visible = i < Math.round(f.v[V.orbs]);
      orb.setEnabled(visible);
      const a = f.t * 1.8 + i * (Math.PI / 2);
      orb.position.set(f.pos.x + Math.cos(a) * 0.72, f.pos.y + 1.35 + Math.sin(f.t * 2 + i) * 0.08, f.pos.z + Math.sin(a) * 0.72);
    }
    for (const eye of gear.eyes) eye.scaling.setAll(1 + Math.sin(f.t * 8) * 0.08);
    gear.aura.setEnabled(f.ultActive);
    gear.aura.position.set(f.pos.x, f.pos.y + 0.06, f.pos.z);
    return f.ultActive ? "holding-both" : undefined;
  },
  hud(c, eid) {
    return [
      { label: "Life Orbs", value: hv(c, eid, V.orbs) / 4, color: "#e4ff7a" },
      { label: "Favor", value: Math.min(1, hv(c, eid, V.passive) / 8), color: "#7ed957" },
    ];
  },
  projectiles: {
    [PK.Thorn]: (scene, f: ProjectileFrame) => {
      const thorn = MeshBuilder.CreateCylinder("mantissa-thorn", { diameterTop: 0.02, diameterBottom: 0.13, height: 0.7, tessellation: 5 }, scene);
      thorn.material = glow(scene, "#d7ff5a", 0.8);
      thorn.position.set(f.pos.x, f.pos.y, f.pos.z);
      return {
        gear: thorn,
        update(m: Mesh, frame) {
          m.position.set(frame.pos.x, frame.pos.y, frame.pos.z);
          m.lookAt(new Vector3(frame.pos.x + frame.dir.x, frame.pos.y + frame.dir.y, frame.pos.z + frame.dir.z));
          m.rotation.x += Math.PI / 2;
        },
        dispose: () => thorn.dispose(),
      };
    },
    [PK.Spore]: (scene, f: ProjectileFrame) => {
      const pod = MeshBuilder.CreateSphere("mantissa-spore", { diameter: 0.36, segments: 10 }, scene);
      pod.material = glow(scene, "#baff6a", 0.72);
      pod.position.set(f.pos.x, f.pos.y, f.pos.z);
      return {
        gear: pod,
        update(m: Mesh, frame) {
          m.position.set(frame.pos.x, frame.pos.y, frame.pos.z);
          m.scaling.setAll(1 + Math.sin(frame.age * 18) * 0.08);
        },
        dispose: () => pod.dispose(),
      };
    },
  },
  icons: {
    [Slot.Primary]: "M4 12l16-6-6 16-2-7z",
    [Slot.Secondary]: "M12 4c5 4 5 12 0 16c-5-4-5-12 0-16zM8 12h8",
    [Slot.Ability1]: "M5 9c5-4 9-4 14 0M7 15c4 3 6 3 10 0M12 9v6",
    [Slot.Ability2]: "M5 16c5-8 9-8 14 0M8 13l4-7 4 7",
    [Slot.Ability3]: "M12 3l3 7 7 2-7 2-3 7-3-7-7-2 7-2z",
    [Slot.Ultimate]: "M4 18c4-10 12-10 16 0M7 14h10M9 10h6",
    passive: "M12 4v16M6 8c4 2 8 2 12 0M6 16c4-2 8-2 12 0",
  },
  audio: {
    weight: "light",
    fire: () => ({ key: "fire:mantissa:thorn-polish", cue: thornMath }),
    ability: { [Slot.Secondary]: kindBloom, [Slot.Ability2]: pepPollen, [Slot.Ability1]: napSpore, [Slot.Ability3]: pricklyMood, [Slot.Ultimate]: gardenOverdrive },
    theme: mantissaTheme,
    themeTrim: { heroic: 2.9, hostile: -6.2, tease: 9.6 },
    flight: { [PK.Thorn]: loops.flight("orb"), [PK.Spore]: loops.flight("orb") },
    loops: ({ ultActive, alive }) => (alive && ultActive ? [{ key: "garden", build: loops.bioticField, gain: 0.85 }] : []),
  },
};

export default hero;
